import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Spinner, Pagination, Table, Badge } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaTimes, FaEdit, FaTrash, FaEye } from "react-icons/fa";
import Swal from 'sweetalert2';

export default function DoctorsIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const AuthToken = localStorage.getItem("authToken");

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [specializations, setSpecializations] = useState([]);

    // Filter states - temporary values from inputs
    const [tempFilters, setTempFilters] = useState({
        name: '',
        role: '',
        status: '',
        day: '',
        per_page: 10
    });

    // Active filters - applied when search button is clicked
    const [activeFilters, setActiveFilters] = useState({
        name: '',
        role: '',
        status: '',
        day: '',
        per_page: 10
    });

    // Pagination states
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    // Days of week for filter
    const daysOfWeek = [
        { value: 'monday', label: 'Monday' },
        { value: 'tuesday', label: 'Tuesday' },
        { value: 'wednesday', label: 'Wednesday' },
        { value: 'thursday', label: 'Thursday' },
        { value: 'friday', label: 'Friday' },
        { value: 'saturday', label: 'Saturday' },
        { value: 'sunday', label: 'Sunday' }
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ];

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    // Fetch doctors with active filters and pagination
    const fetchDoctors = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: activeFilters.per_page,
                ...(activeFilters.name && { name: activeFilters.name }),
                ...(activeFilters.role && { role: activeFilters.role }),
                ...(activeFilters.status && { status: activeFilters.status }),
                ...(activeFilters.day && { day: activeFilters.day })
            });

            const res = await axios.get(`${process.env.REACT_APP_API_URL}/doctors?${params}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (res.data.status) {
                setData(res.data.doctors.data || []);
                setSpecializations(res.data.specializations || []);
                setPagination({
                    current_page: res.data.doctors.current_page || 1,
                    last_page: res.data.doctors.last_page || 1,
                    per_page: res.data.doctors.per_page || 10,
                    total: res.data.doctors.total || 0,
                    from: res.data.doctors.from || 0,
                    to: res.data.doctors.to || 0
                });
            }
        } catch (err) {
            console.error(err);
            showErrorToast(t('Failed to fetch doctors'));
        } finally {
            setLoading(false);
        }
    };

    // Fetch when active filters or pagination changes
    useEffect(() => {
        fetchDoctors();
    }, [activeFilters.name, activeFilters.role, activeFilters.status, activeFilters.day, activeFilters.per_page, pagination.current_page]);

    // Handle filter input changes (temporary)
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply search - move temp filters to active filters
    const applySearch = () => {
        setActiveFilters({
            name: tempFilters.name,
            role: tempFilters.role,
            status: tempFilters.status,
            day: tempFilters.day,
            per_page: tempFilters.per_page
        });
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        const emptyFilters = {
            name: '',
            role: '',
            status: '',
            day: '',
            per_page: 10
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        const value = parseInt(e.target.value);
        setTempFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setActiveFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setPagination(prev => ({
                ...prev,
                current_page: page
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle Enter key press in search inputs
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            applySearch();
        }
    };

    // Handle delete with SweetAlert
    const handleDelete = (id, name) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t(`Want to delete doctor "${name}"?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await axios.delete(`${process.env.REACT_APP_API_URL}/doctors/${id}`, {
                        headers: { Authorization: `Bearer ${AuthToken}` }
                    });
                    if (response.data.status) {
                        // Check if current page has no items after deletion
                        if (data.length === 1 && pagination.current_page > 1) {
                            setPagination(prev => ({ ...prev, current_page: pagination.current_page - 1 }));
                        } else {
                            fetchDoctors();
                        }
                        showSuccessToast(t('Doctor deleted successfully!'));
                        return true;
                    }
                } catch (error) {
                    console.error(error);
                    showErrorToast(t('Failed to delete doctor'));
                    return false;
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: t('Deleted!'),
                    text: t('Doctor has been deleted.'),
                    icon: 'success',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        });
    };

    // Render Bootstrap Pagination
    const renderBootstrapPagination = () => {
        let items = [];
        let maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    {1}
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.last_page) {
            if (endPage < pagination.last_page - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.last_page}
                    onClick={() => handlePageChange(pagination.last_page)}
                >
                    {pagination.last_page}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
            />
        );

        return items;
    };

    if (loading && !data.length) return <PageLayout><div className="text-center p-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("loading")}...</span></div><p className="mt-3">{t("loading")}...</p></div></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('Staff')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('Staff')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('list')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('Staff list')}</h5>
                            <ButtonComponent
                                type="button"
                                className="btn btn-primary d-inline-flex align-items-center gap-1"
                                onClick={() => navigate(`/doctor-create`)}
                            >
                                <FaPlus size={14} />
                                {t('add Staff')}
                            </ButtonComponent>
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom bg-light">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Name')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            size="sm"
                                            value={tempFilters.name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t("Search by name...")}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Role')}</Form.Label>
                                        <Form.Select
                                            name="role"
                                            size="sm"
                                            value={tempFilters.role}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">{t('All Roles')}</option>
                                            <option value="doctor">{t('Doctor')}</option>
                                            <option value="sweeper">{t('Sweeper')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Status')}</Form.Label>
                                        <Form.Select
                                            name="status"
                                            size="sm"
                                            value={tempFilters.status}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">{t('All Status')}</option>
                                            {statusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {t(option.label)}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t('Per Page')}</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={tempFilters.per_page}
                                            onChange={handlePerPageChange}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={12}>
                                    <div className="d-flex gap-2 justify-content-end">
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={applySearch}
                                        >
                                            <FaSearch size={12} /> {t("Search")}
                                        </ButtonComponent>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary btn-sm d-inline-flex align-items-center gap-1"
                                            onClick={clearFilters}
                                        >
                                            <FaTimes size={12} /> {t("Clear")}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <small className="text-muted">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("Staff")}
                            </small>
                            {loading && (
                                <small>
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </small>
                            )}
                        </div>

                        {/* Table - Using Bootstrap Table with global classes */}
                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '15%' }}>{t('name')}</th>
                                        <th style={{ width: '15%' }}>{t('Role')}</th>
                                        <th style={{ width: '20%' }}>{t('email')}</th>
                                        <th className="text-center" style={{ width: '20%' }}>{t('actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length > 0 ? (
                                        data.map((item, index) => (
                                            <tr key={item.id}>
                                                <td className="text-center">
                                                    {(pagination.current_page - 1) * pagination.per_page + index + 1}
                                                </td>
                                                <td>
                                                    <strong className="text-primary">{item.name}</strong>
                                                </td>
                                                <td>
                                                    {item.role ? (
                                                        <Badge bg={item.role === 'doctor' ? 'primary' : 'success'}>
                                                            {item.role.charAt(0).toUpperCase() + item.role.slice(1)}
                                                        </Badge>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td>{item.email || '-'}</td>

                                                <td>
                                                    <div className="mc-table-action justify-content-center">
                                                        <ButtonComponent
                                                            type="button"
                                                            className="material-icons edit"
                                                            onClick={() => navigate(`/doctor-edit/${item.id}`)}
                                                            title={t("Edit")}
                                                        >
                                                            <FaEdit />
                                                        </ButtonComponent>
                                                        {/* Delete button commented out as per your code */}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-5">
                                                <div className="empty-state">
                                                    <p className="text-muted mb-0">{t('No Staff found')}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0">
                                    {renderBootstrapPagination()}
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}